/* eslint-disable no-await-in-loop */
import AWS from 'aws-sdk';
import { ref, getDatabase, off, onValue, update } from 'firebase/database';
import { toast } from 'react-toastify';
import { UPLOAD_CONFIG } from '../Config/uploadConfig';
import { awsConfig, generateS3Url, getBucketName } from '../Api/awsConfig';

// AWS Configuration
AWS.config.update({
  region: awsConfig.region,
  accessKeyId: awsConfig.accessKey,
  secretAccessKey: awsConfig.secretKey,
});
AWS.config.httpOptions.timeout = 0;
const s3 = new AWS.S3();

// Upload states
export const UPLOAD_STATUS = {
  PENDING: 'pending',
  INITIALIZING: 'initializing',
  UPLOADING: 'uploading',
  PAUSED: 'paused',
  FINISHING: 'finishing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  CANCELLING: 'cancelling',
  NETWORK_ERROR: 'network_error',
};

/**
 * Helper function to create dummy object for display during upload
 */
const createDummy = (options) => {
  const {
    draft_id,
    title,
    fileName,
    thumbnail,
    ForNoImage,
    category,
    description,
    videoSize,
    selectedGroups,
    changeSize,
  } = options;

  // Clean up thumbnail URL - remove any extra commas or whitespace
  const cleanThumbnail = thumbnail
    ? thumbnail.replace(/,+$/, '').trim()
    : ForNoImage;

  return {
    draft_id,
    title: title || fileName,
    thumbnail: cleanThumbnail,
    category,
    video_description: description,
    video_size: changeSize ? changeSize(videoSize) : videoSize,
    type: 0,
    group_id: selectedGroups,
    content_status: 'Video',
    uploadIsTrying: 1,
  };
};

/**
 * Enhanced Upload Manager with pause/resume and network detection
 */
class UploadManager {
  constructor() {
    this.uploads = new Map(); // uploadId -> UploadTask
    this.listeners = new Set();
    this.networkStatus = 'online';
    this.networkCheckInterval = null;
    this.startNetworkMonitoring();
  }

  /**
   * Get count of active uploads (uploading, initializing, finishing)
   */
  getActiveUploadCount() {
    let count = 0;
    this.uploads.forEach((task) => {
      if (
        task.status === UPLOAD_STATUS.UPLOADING
        || task.status === UPLOAD_STATUS.INITIALIZING
        || task.status === UPLOAD_STATUS.FINISHING
      ) {
        count += 1;
      }
    });
    return count;
  }

  /**
   * Get total pending items count (videos + remaining photos)
   * For videos: count each video as 1 item
   * For photos: count remaining photos (totalPhotos - uploadedPhotos)
   * Includes: pending, initializing, uploading, paused, network_error, finishing
   */
  getPendingItemsCount() {
    let totalPending = 0;

    this.uploads.forEach((task) => {
      const isActive = task.status === UPLOAD_STATUS.PENDING
        || task.status === UPLOAD_STATUS.INITIALIZING
        || task.status === UPLOAD_STATUS.UPLOADING
        || task.status === UPLOAD_STATUS.PAUSED
        || task.status === UPLOAD_STATUS.NETWORK_ERROR
        || task.status === UPLOAD_STATUS.FINISHING;

      if (isActive) {
        if (task.uploadType === 'photo') {
          // For photos, count remaining photos
          const remaining = (task.totalPhotos || 0) - (task.uploadedPhotos || 0);
          totalPending += remaining;
        } else {
          // For videos, count as 1 item
          totalPending += 1;
        }
      }
    });

    return totalPending;
  }

  /**
   * Get currently uploading items count (includes pending/queued items)
   * For videos: count each uploading/pending video as 1 item
   * For photos: count remaining photos in uploading/pending albums
   * Includes: pending, initializing, uploading, paused, network_error, finishing
   */
  getCurrentlyUploadingCount() {
    let totalUploading = 0;

    this.uploads.forEach((task) => {
      const isActive = task.status === UPLOAD_STATUS.PENDING
        || task.status === UPLOAD_STATUS.UPLOADING
        || task.status === UPLOAD_STATUS.INITIALIZING
        || task.status === UPLOAD_STATUS.FINISHING
        || task.status === UPLOAD_STATUS.PAUSED
        || task.status === UPLOAD_STATUS.NETWORK_ERROR;

      if (isActive) {
        if (task.uploadType === 'photo') {
          // For photos, count remaining photos
          const remaining = (task.totalPhotos || 0) - (task.uploadedPhotos || 0);
          totalUploading += remaining;
        } else {
          // For videos, count as 1 item
          totalUploading += 1;
        }
      }
    });

    return totalUploading;
  }

  /**
   * Start next upload in queue if slot available
   */
  startNextInQueue() {
    const activeCount = this.getActiveUploadCount();

    if (activeCount >= UPLOAD_CONFIG.MAX_CONCURRENT_UPLOADS) {
      return; // No slots available
    }

    // Find first pending upload
    let nextUpload = null;
    this.uploads.forEach((task) => {
      if (!nextUpload && task.status === UPLOAD_STATUS.PENDING) {
        nextUpload = task;
      }
    });

    if (nextUpload) {
      console.log(`Starting queued upload: ${nextUpload.fileName}`);
      this.startUpload(nextUpload.id);
    }
  }

  /**
   * Start monitoring network status
   */
  startNetworkMonitoring() {
    // Listen to online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    // Periodic check
    this.networkCheckInterval = setInterval(() => {
      this.checkNetworkStatus();
    }, UPLOAD_CONFIG.NETWORK_CHECK_INTERVAL);
  }

  handleOnline() {
    console.log('Network: ONLINE');
    this.networkStatus = 'online';
    this.notifyListeners();

    // Auto-resume paused uploads due to network
    this.uploads.forEach((task) => {
      if (task.status === UPLOAD_STATUS.NETWORK_ERROR) {
        console.log(`Auto-resuming upload ${task.id} after network restore`);
        // If upload had never started (was pending), restore to pending state
        if (!task.uploadIdS3 && task.currentPartNumber === 0) {
          const updatedTask = task;
          updatedTask.status = UPLOAD_STATUS.PENDING;
          updatedTask.isPaused = false;
          this.notifyListeners();
          // Let the queue system handle starting it
          this.startNextInQueue();
        } else {
          // Otherwise resume the actual upload
          this.resumeUpload(task.id);
        }
      }
    });
  }

  handleOffline() {
    console.log('Network: OFFLINE');
    this.networkStatus = 'offline';
    this.notifyListeners();

    // Auto-pause all active uploads and mark pending as network error
    this.uploads.forEach((task) => {
      if (task.status === UPLOAD_STATUS.UPLOADING || task.status === UPLOAD_STATUS.PENDING) {
        console.log(`Auto-pausing upload ${task.id} due to network loss`);
        const updatedTask = task;
        updatedTask.status = UPLOAD_STATUS.NETWORK_ERROR;
        updatedTask.isPaused = true;
        this.notifyListeners();
      }
    });
  }

  async checkNetworkStatus() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      await fetch(UPLOAD_CONFIG.NETWORK_CHECK_URL, {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (this.networkStatus === 'offline') {
        this.handleOnline();
      }
    } catch (error) {
      if (this.networkStatus === 'online') {
        this.handleOffline();
      }
    }
  }

  /**
   * Subscribe to upload updates
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of state changes
   */
  notifyListeners() {
    const uploadList = Array.from(this.uploads.values()).map((task) => ({
      id: task.id,
      draftId: task.draftId,
      uploadType: task.uploadType || 'video',
      fileName: task.fileName,
      file: task.file,
      status: task.status,
      progress: task.progress,
      bytesUploaded: task.bytesUploaded,
      totalBytes: task.totalBytes,
      uploadedPhotos: task.uploadedPhotos,
      totalPhotos: task.totalPhotos,
      speed: task.speed,
      timeRemaining: task.timeRemaining,
      error: task.error,
      isPaused: task.isPaused,
      metadata: task.metadata,
    }));

    this.listeners.forEach((callback) => {
      callback({
        uploads: uploadList,
        networkStatus: this.networkStatus,
      });
    });
  }

  /**
   * Create a photo album upload task (external upload, just tracking)
   */
  createPhotoUpload(options) {
    const {
      albumId,
      albumTitle,
      photoCount,
      updateUploadPercentages,
    } = options;

    const uploadId = albumId;

    const task = {
      id: uploadId,
      draftId: albumId,
      uploadType: 'photo', // Mark as photo upload
      albumTitle,
      fileName: albumTitle || 'Photo Album',
      totalPhotos: photoCount,
      uploadedPhotos: 0,
      progress: 0,
      speed: 0,
      timeRemaining: 0,
      status: UPLOAD_STATUS.UPLOADING,
      isPaused: false,
      error: null,
      updateUploadPercentages,
      startTime: Date.now(),
      lastUpdateTime: Date.now(),
    };

    this.uploads.set(uploadId, task);

    // Update status
    if (updateUploadPercentages) {
      updateUploadPercentages(albumId, `0/${photoCount}`);
    }

    this.notifyListeners();

    return uploadId;
  }

  /**
   * Update photo upload progress
   */
  updatePhotoUploadProgress(uploadId, uploadedCount, totalCount) {
    const task = this.uploads.get(uploadId);
    if (!task || task.uploadType !== 'photo') return;

    task.uploadedPhotos = uploadedCount;
    task.totalPhotos = totalCount;
    task.progress = Math.round((uploadedCount / totalCount) * 100);

    task.lastUpdateTime = Date.now();

    // Update status
    if (task.updateUploadPercentages) {
      task.updateUploadPercentages(uploadId, `${uploadedCount}/${totalCount}`);
    }

    this.notifyListeners();
  }

  /**
   * Complete photo upload
   */
  completePhotoUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task || task.uploadType !== 'photo') return;

    task.status = UPLOAD_STATUS.COMPLETED;
    task.progress = 100;

    // Clear status
    if (task.updateUploadPercentages) {
      task.updateUploadPercentages(uploadId, null);
    }

    this.notifyListeners();

    // Auto-remove completed upload after delay
    if (UPLOAD_CONFIG.AUTO_HIDE_COMPLETED) {
      setTimeout(() => {
        this.removeUpload(uploadId);
      }, UPLOAD_CONFIG.AUTO_HIDE_DELAY);
    }
  }

  /**
   * Fail photo upload
   */
  failPhotoUpload(uploadId, errorMessage) {
    const task = this.uploads.get(uploadId);
    if (!task || task.uploadType !== 'photo') return;

    task.status = UPLOAD_STATUS.FAILED;
    task.error = errorMessage || 'Upload failed';

    // Clear status
    if (task.updateUploadPercentages) {
      task.updateUploadPercentages(uploadId, null);
    }

    this.notifyListeners();
  }

  /**
   * Create and start a new upload
   */
  async createUpload(options) {
    const {
      bucket,
      key,
      file,
      draftId,
      metadata,
      onProgress,
      onSuccess,
      onError,
      updateUploadPercentages,
      updateUploadDetails,
      setUploadContainer,
      setUploadContainer2,
      updateDummy,
      dummyData,
    } = options;

    const uploadId = draftId;

    // Create upload task
    // Check if we're at the concurrent upload limit
    const activeCount = this.getActiveUploadCount();
    const shouldQueue = activeCount >= UPLOAD_CONFIG.MAX_CONCURRENT_UPLOADS;

    const task = {
      id: uploadId,
      draftId,
      uploadType: 'video', // Mark as video upload
      bucket,
      key,
      file,
      fileName: file.name,
      totalBytes: file.size,
      bytesUploaded: 0,
      progress: 0,
      speed: 0,
      smoothedSpeed: 0, // Smoothed speed for better UX
      timeRemaining: 0,
      status: shouldQueue ? UPLOAD_STATUS.PENDING : UPLOAD_STATUS.INITIALIZING,
      isPaused: false,
      error: null,
      metadata,
      onProgress,
      onSuccess,
      onError,
      updateUploadPercentages,
      updateUploadDetails,
      setUploadContainer,
      setUploadContainer2,
      updateDummy,
      dummyData,
      // Upload state
      uploadIdS3: null,
      parts: [],
      currentPartNumber: 0,
      startTime: Date.now(),
      lastUpdateTime: Date.now(),
      lastSpeedUpdateTime: Date.now(),
      lastBytesUploaded: 0,
      retryCount: 0,
    };

    this.uploads.set(uploadId, task);

    // Add to upload containers
    if (setUploadContainer) {
      setUploadContainer((prev) => [...prev, draftId]);
    }
    if (setUploadContainer2) {
      setUploadContainer2((prev) => [...prev, draftId]);
    }

    // Create dummy if provided
    if (dummyData && updateDummy) {
      const dummy = createDummy({
        ...dummyData,
        draft_id: draftId,
      });
      updateDummy(dummy, 1);
    }

    // Show toast notification
    const trimmedFilename = file.name.length > 26 ? `${file.name.substring(0, 26)}...` : file.name;
    toast(trimmedFilename, {
      autoClose: false,
      toastId: draftId,
      position: toast.POSITION.BOTTOM_RIGHT,
      closeButton: false,
    });

    // Update status
    if (updateUploadPercentages) {
      if (shouldQueue) {
        updateUploadPercentages(draftId, 'In queue...');
      } else {
        updateUploadPercentages(draftId, 'Initializing');
      }
    }

    this.notifyListeners();

    // Start upload immediately if not queued
    if (!shouldQueue) {
      this.startUpload(uploadId);
    }

    return uploadId;
  }

  /**
   * Start or resume upload
   */
  async startUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    try {
      // Check network
      if (this.networkStatus === 'offline') {
        task.status = UPLOAD_STATUS.NETWORK_ERROR;
        task.isPaused = true;
        this.notifyListeners();
        return;
      }

      task.status = UPLOAD_STATUS.UPLOADING;
      task.isPaused = false;

      // Only set start time on initial upload, not on resume
      if (!task.startTime || task.startTime === 0) {
        task.startTime = Date.now();
      }

      // Reset tracking for speed calculation when resuming
      task.lastUpdateTime = Date.now();
      task.lastSpeedUpdateTime = Date.now();
      task.lastBytesUploaded = task.bytesUploaded; // Start from current position on resume
      task.smoothedSpeed = 0; // Reset smoothed speed for clean recalculation

      this.notifyListeners();

      // Initialize multipart upload if not already done
      if (!task.uploadIdS3) {
        const initResponse = await s3
          .createMultipartUpload({
            Bucket: task.bucket,
            Key: task.key,
          })
          .promise();

        task.uploadIdS3 = initResponse.UploadId;
      }

      // Calculate part size and count
      const minimumPartSize = UPLOAD_CONFIG.CHUNK_SIZE;
      let partSize = task.file.size / 100;
      if (partSize < minimumPartSize) {
        partSize = minimumPartSize;
      }

      const totalParts = Math.ceil(task.file.size / partSize);

      // Update status
      if (task.updateUploadPercentages) {
        task.updateUploadPercentages(task.draftId, 'Starting Upload');
      }

      // Upload parts sequentially
      for (
        let partNumber = task.currentPartNumber + 1;
        partNumber <= totalParts;
        partNumber += 1
      ) {
        // Check if paused or cancelled
        if (task.isPaused || task.status === UPLOAD_STATUS.CANCELLED) {
          task.currentPartNumber = partNumber - 1;
          return;
        }

        // Check network
        if (this.networkStatus === 'offline') {
          task.status = UPLOAD_STATUS.NETWORK_ERROR;
          task.isPaused = true;
          task.currentPartNumber = partNumber - 1;
          this.notifyListeners();
          return;
        }

        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, task.file.size);
        const partBody = task.file.slice(start, end);

        // Upload part with retry
        let uploadSuccess = false;
        let retryCount = 0;

        while (!uploadSuccess && retryCount < UPLOAD_CONFIG.RETRY_ATTEMPTS) {
          try {
            // Track bytes uploaded at the start of this part
            const bytesBeforePart = start;

            // Create upload request with progress tracking
            const uploadRequest = s3.uploadPart({
              Bucket: task.bucket,
              Key: task.key,
              PartNumber: partNumber,
              UploadId: task.uploadIdS3,
              Body: partBody,
            });

            // Track progress within this chunk for smooth updates
            uploadRequest.on('httpUploadProgress', (progress) => {
              // Calculate total bytes uploaded (previous parts + current part progress)
              const currentPartUploaded = progress.loaded || 0;
              let totalUploaded = bytesBeforePart + currentPartUploaded;

              // Ensure progress never goes backwards (important for pause/resume)
              totalUploaded = Math.max(totalUploaded, task.bytesUploaded);

              // Update task progress smoothly
              task.bytesUploaded = totalUploaded;
              task.progress = Math.min(
                100,
                Math.round((totalUploaded / task.totalBytes) * 100)
              );

              // Calculate speed and time remaining
              const currentTime = Date.now();
              const timeDiffProgress = (currentTime - task.lastUpdateTime) / 1000; // seconds
              const timeDiffSpeed = (currentTime - task.lastSpeedUpdateTime) / 1000; // seconds

              // Update progress UI at 200ms intervals (smooth progress bar)
              const shouldUpdateProgress = timeDiffProgress >= UPLOAD_CONFIG.PROGRESS_UPDATE_INTERVAL / 1000;

              // Update speed/ETA at 1.5s intervals (stable numbers)
              const shouldUpdateSpeed = timeDiffSpeed >= UPLOAD_CONFIG.SPEED_UPDATE_INTERVAL / 1000;

              if (shouldUpdateSpeed) {
                const bytesDiff = totalUploaded - task.lastBytesUploaded;

                // Only calculate speed if there's actual progress (avoids zero speed during catch-up)
                if (bytesDiff > 0) {
                  const instantSpeed = bytesDiff / timeDiffSpeed; // bytes per second

                  // Apply exponential smoothing for smoother speed display (feels more natural)
                  // Alpha value creates ~500-1000ms smoothing effect
                  const alpha = 0.3; // Lower = smoother, higher = more responsive
                  task.smoothedSpeed = task.smoothedSpeed === 0
                    ? instantSpeed
                    : alpha * instantSpeed + (1 - alpha) * task.smoothedSpeed;

                  task.speed = task.smoothedSpeed;
                  task.timeRemaining = (task.totalBytes - totalUploaded) / task.speed; // seconds
                }

                task.lastSpeedUpdateTime = currentTime;
                task.lastBytesUploaded = totalUploaded;
              }

              if (shouldUpdateProgress) {
                task.lastUpdateTime = currentTime;

                // Update UI
                if (task.updateUploadPercentages) {
                  task.updateUploadPercentages(task.draftId, task.progress);
                }

                // Update toast
                const trimmedFilename = task.fileName.length > 26
                    ? `${task.fileName.substring(0, 26)}...`
                    : task.fileName;
                toast.update(task.draftId, {
                  render: `${trimmedFilename} ${task.progress}%`,
                  autoClose: false,
                });

                // Call progress callback
                if (task.onProgress) {
                  task.onProgress(task.progress, totalUploaded, task.totalBytes);
                }

                // Notify listeners for upload panel updates
                this.notifyListeners();
              }

              // Update upload details when speed is updated
              if (shouldUpdateSpeed && task.updateUploadDetails) {
                const elapsedSize = totalUploaded / 1024 / 1024; // MB
                const elapsedTime = (currentTime - task.startTime) / 1000; // seconds
                const remainingSize = (task.totalBytes - totalUploaded) / 1024 / 1024; // MB

                task.updateUploadDetails(task.draftId, {
                  elapsed_size: elapsedSize,
                  elapsed_time: elapsedTime,
                  remaining_size: remainingSize > 0 ? remainingSize : 0,
                  speed: task.speed,
                  time_remaining: task.timeRemaining,
                });
              }
            });

            // Execute the upload
            const partData = await uploadRequest.promise();

            task.parts.push({
              PartNumber: partNumber,
              ETag: partData.ETag,
            });

            // Final update for this part (already updated via httpUploadProgress)
            task.currentPartNumber = partNumber;

            // One final notification to ensure UI is in sync
            this.notifyListeners();
            uploadSuccess = true;
          } catch (error) {
            retryCount += 1;
            console.error(`Error uploading part ${partNumber}, retry ${retryCount}:`, error);

            if (retryCount >= UPLOAD_CONFIG.RETRY_ATTEMPTS) {
              throw error;
            }

            // Wait before retry
            await new Promise((resolve) => setTimeout(resolve, UPLOAD_CONFIG.RETRY_DELAY));
          }
        }
      }

      // Complete multipart upload
      await s3
        .completeMultipartUpload({
          Bucket: task.bucket,
          Key: task.key,
          UploadId: task.uploadIdS3,
          MultipartUpload: {
            Parts: task.parts,
          },
        })
        .promise();

      // S3 Upload completed, now finishing up (API call)
      task.status = UPLOAD_STATUS.FINISHING;
      task.progress = 100;
      this.notifyListeners();

      // Update UI
      if (task.updateUploadPercentages) {
        task.updateUploadPercentages(task.draftId, 'Please wait');
      }

      toast.update(task.draftId, {
        render: 'Finalizing upload...',
        type: toast.TYPE.INFO,
        autoClose: false,
        closeButton: false,
      });

      // Call success callback (API call happens here)
      if (task.onSuccess) {
        await task.onSuccess(task.draftId);
      }

      // Now fully completed
      task.status = UPLOAD_STATUS.COMPLETED;
      this.notifyListeners();

      toast.update(task.draftId, {
        render: 'Upload completed!',
        type: toast.TYPE.SUCCESS,
        autoClose: 3000,
        closeButton: false,
      });

      // Remove from upload containers
      if (task.setUploadContainer) {
        task.setUploadContainer((prev) => prev.filter((id) => id !== task.draftId));
      }
      if (task.setUploadContainer2) {
        task.setUploadContainer2((prev) => prev.filter((id) => id !== task.draftId));
      }

      // Auto-remove completed upload after delay
      if (UPLOAD_CONFIG.AUTO_HIDE_COMPLETED) {
        setTimeout(() => {
          this.removeUpload(uploadId);
        }, UPLOAD_CONFIG.AUTO_HIDE_DELAY);
      }

      // Start next upload in queue
      this.startNextInQueue();
    } catch (error) {
      console.error('Upload error:', error);

      // Check if it's a NoSuchUpload error (upload ID expired/invalidated)
      const isNoSuchUpload = error.code === 'NoSuchUpload'
        || (error.message && error.message.includes('NoSuchUpload'))
        || (error.message && error.message.includes('does not exist'));

      if (isNoSuchUpload && task.retryCount < 1) {
        // Upload ID is invalid - restart from beginning with fresh multipart upload
        console.log(`Upload ID expired for ${task.fileName}, restarting with fresh upload...`);
        task.retryCount += 1;

        // Reset upload state to start fresh
        task.uploadIdS3 = null;
        task.parts = [];
        task.currentPartNumber = 0;
        task.bytesUploaded = 0;
        task.progress = 0;

        // Restart the upload
        task.status = UPLOAD_STATUS.INITIALIZING;
        this.notifyListeners();

        // Retry upload
        this.startUpload(uploadId);
        return;
      }

      task.status = UPLOAD_STATUS.FAILED;
      task.error = error.message || 'Upload failed';

      // Update UI
      if (task.updateUploadPercentages) {
        task.updateUploadPercentages(task.draftId, 'Cancelled');
      }

      toast.update(task.draftId, {
        render: 'Upload failed',
        type: toast.TYPE.ERROR,
        autoClose: 3000,
        closeButton: false,
      });

      // Remove from upload containers
      if (task.setUploadContainer) {
        task.setUploadContainer((prev) => prev.filter((id) => id !== task.draftId));
      }
      if (task.setUploadContainer2) {
        task.setUploadContainer2((prev) => prev.filter((id) => id !== task.draftId));
      }

      // Call error callback
      if (task.onError) {
        task.onError(error, task.draftId);
      }

      this.notifyListeners();

      // Start next upload in queue
      this.startNextInQueue();
    }
  }

  /**
   * Pause upload
   */
  pauseUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    if (task.status === UPLOAD_STATUS.UPLOADING) {
      task.status = UPLOAD_STATUS.PAUSED;
      task.isPaused = true;
      this.notifyListeners();
    }
  }

  /**
   * Resume upload
   */
  resumeUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    if (
      task.status === UPLOAD_STATUS.PAUSED
      || task.status === UPLOAD_STATUS.NETWORK_ERROR
    ) {
      if (this.networkStatus === 'offline') {
        toast.error('Cannot resume: No network connection');
        return;
      }

      task.status = UPLOAD_STATUS.UPLOADING;
      task.isPaused = false;
      this.startUpload(uploadId);
    }
  }

  /**
   * Cancel upload
   */
  async cancelUpload(uploadId) {
    const task = this.uploads.get(uploadId);
    if (!task) return;

    // Set status to cancelling first
    task.status = UPLOAD_STATUS.CANCELLING;
    task.isPaused = true;
    this.notifyListeners();

    // Abort S3 multipart upload if started
    if (task.uploadIdS3) {
      try {
        await s3
          .abortMultipartUpload({
            Bucket: task.bucket,
            Key: task.key,
            UploadId: task.uploadIdS3,
          })
          .promise();
      } catch (error) {
        console.error('Error aborting multipart upload:', error);
      }
    }

    // Update to cancelled
    task.status = UPLOAD_STATUS.CANCELLED;

    // Update UI
    if (task.updateUploadPercentages) {
      task.updateUploadPercentages(task.draftId, 'Cancelled');
    }

    toast.update(task.draftId, {
      render: 'Upload cancelled',
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: false,
    });

    // Remove from upload containers
    if (task.setUploadContainer) {
      task.setUploadContainer((prev) => prev.filter((id) => id !== task.draftId));
    }
    if (task.setUploadContainer2) {
      task.setUploadContainer2((prev) => prev.filter((id) => id !== task.draftId));
    }

    // Remove dummy
    if (task.updateDummy) {
      task.updateDummy(task.draftId, 0);
    }

    this.notifyListeners();

    // Remove from manager after a short delay to show "cancelled" state
    setTimeout(() => {
      this.uploads.delete(uploadId);
      this.notifyListeners();

      // Start next upload in queue
      this.startNextInQueue();
    }, 500);
  }

  /**
   * Remove upload from manager
   */
  removeUpload(uploadId) {
    this.uploads.delete(uploadId);
    this.notifyListeners();
  }

  /**
   * Get all uploads
   */
  getAllUploads() {
    return Array.from(this.uploads.values()).map((task) => ({
      id: task.id,
      draftId: task.draftId,
      fileName: task.fileName,
      status: task.status,
      progress: task.progress,
      bytesUploaded: task.bytesUploaded,
      totalBytes: task.totalBytes,
      speed: task.speed,
      timeRemaining: task.timeRemaining,
      error: task.error,
      isPaused: task.isPaused,
      metadata: task.metadata,
    }));
  }

  /**
   * Get upload by ID
   */
  getUpload(uploadId) {
    return this.uploads.get(uploadId);
  }

  /**
   * Cleanup
   */
  destroy() {
    clearInterval(this.networkCheckInterval);
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
    this.uploads.clear();
    this.listeners.clear();
  }
}

// Create singleton instance
const uploadManager = new UploadManager();

export default uploadManager;

/**
 * Wrapper function for compatibility with existing code
 */
export async function uploadLargeFileNew(options) {
  return uploadManager.createUpload(options);
}
